import { useEffect } from "react";
import { useField, useFormikContext } from "formik";
import { parsePhoneNumberWithError } from "libphonenumber-js";
import isEqual from "lodash/isEqual";
import * as Yup from "yup";
import { type ValidationError } from "yup";

import { m } from "@vega-core/paraglide/messages";
import { isTruthy } from "@vega-core/utils/isTruthy";

/**
 * Temporary code to cover BE validation for zip codes
 */
const ZIP_CODE_PATTERNS = {
  CA: "[ABCEGHJKLMNPRSTVXY]\\d[ABCEGHJ-NPRSTV-Z] ?\\d[ABCEGHJ-NPRSTV-Z]\\d",
  GE: "\\d{4}",
  FR: "\\d{2} ?\\d{3}",
  US: "(\\d{5})(?:[ \\-](\\d{4}))?",
  GB: "([Gg][Ii][Rr] 0[Aa]{2})|((([A-Za-z][0-9]{1,2})|(([A-Za-z][A-Ha-hJ-Yj-y][0-9]{1,2})|(([A-Za-z][0-9][A-Za-z])|([A-Za-z][A-Ha-hJ-Yj-y][0-9][A-Za-z]?))))\\s?[0-9][A-Za-z]{2})",
};
const DEFAULT_ZIP_CODE_PATTERN = "^(?!.*--)[A-Za-z0-9-]{1,12}$";

type CountryCode = keyof typeof ZIP_CODE_PATTERNS;

export const validateZipCode = (
  countryCode?: CountryCode,
  zipcode?: string,
): boolean => {
  if (!isTruthy(countryCode) || !isTruthy(zipcode)) {
    return true;
  }

  const pattern = ZIP_CODE_PATTERNS[countryCode] || DEFAULT_ZIP_CODE_PATTERN;
  const regex = new RegExp(`^${pattern}$`);

  return regex.test(zipcode);
};

/**
 * Get zipOptionalCountryNames from localStorage
 */
const getZipOptionalCountryNames = (): Record<string, string | undefined> => {
  try {
    const stored = localStorage.getItem("zipOptionalCountryNames");

    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
};

/**
 * Check if zipcode is optional for a given country
 */
export const isZipcodeOptionalForCountry = (countryName: string): boolean => {
  const zipOptionalCountryNames = getZipOptionalCountryNames();

  return (
    Object.values(zipOptionalCountryNames).includes(countryName) ||
    Object.keys(zipOptionalCountryNames).includes(countryName)
  );
};

export const getFileUploadValidation = () =>
  Yup.array()
    .of(
      Yup.object()
        .required(
          m.formValidation_required({
            name: m.formFields_fileUpload(),
          }),
        )
        .nullable(),
    )
    .min(1, m.formValidation_required({ name: m.formFields_fileUpload() }))
    .required(
      m.formValidation_required({
        name: m.formFields_fileUpload(),
      }),
    );
export const getSingleFileUploadValidation = () =>
  Yup.object()
    .required(
      m.formValidation_required({
        name: m.formFields_fileUpload(),
      }),
    )
    .nullable();

export const getPhoneValidationSchema = (isRequired = true) => {
  const errorMessage = m.formValidation_required({
    name: m.formFields_phoneNumber(),
  });
  const requiredSchema = Yup.string().required(errorMessage).nullable();

  const optionalSchema = Yup.string().optional().nullable();

  return isRequired ? requiredSchema : optionalSchema;
};

export const E164_PHONE_REGEX = /^\+[1-9][0-9]{0,24}$/;

export const isValidE164PhoneNumber = (
  value: string | null | undefined,
): boolean => {
  if (!isTruthy(value)) return true;

  const stripped = value.replace(/[\s-]/g, "");

  if (!E164_PHONE_REGEX.test(stripped)) return false;

  try {
    const parsed = parsePhoneNumberWithError(stripped);

    return Boolean(parsed.countryCallingCode);
  } catch {
    return false;
  }
};

export const getE164PhoneValidationSchema = (isRequired = true) =>
  getPhoneValidationSchema(isRequired).test(
    "e164",
    m.formValidation_phoneE164(),
    isValidE164PhoneNumber,
  );

/**
 * Maximum allowed length for email addresses (RFC 5321)
 */
const MAX_EMAIL_LENGTH = 320;

/**
 * Regex pattern for valid local part characters (before @)
 * Allows: letters, numbers, dots, hyphens, underscores, plus signs
 */
const LOCAL_PART_PATTERN = /^[a-zA-Z0-9._+-]+$/;

/**
 * Regex pattern for valid domain characters (after @)
 * Allows: letters, numbers, dots, hyphens
 */
const DOMAIN_PATTERN = /^[a-zA-Z0-9.-]+$/;

/**
 * Validates email format according to specified business rules.
 * Empty values are considered valid (handled by required validation separately).
 *
 * @param value - The email string to validate (can be null or undefined)
 * @returns true if the email format is valid, false otherwise
 */
export const validateEmailFormat = (
  value: string | null | undefined,
): boolean => {
  // Empty values are handled by required validation
  if (!value || value.trim() === "") {
    return true;
  }

  const email = value.trim();

  // Early return for length check
  if (email.length > MAX_EMAIL_LENGTH) {
    return false;
  }

  // Early return for spaces
  if (/\s/.test(email)) {
    return false;
  }

  // Validate exactly one @ symbol
  const atSymbols = email.match(/@/g);

  if (!atSymbols || atSymbols.length !== 1) {
    return false;
  }

  const [localPart, domainPart] = email.split("@");

  // Validate local part (before @)
  if (!isValidLocalPart(localPart)) {
    return false;
  }

  // Validate domain part (after @)
  return isValidDomainPart(domainPart);
};

/**
 * Validates the local part of an email address (before @).
 *
 * @param localPart - The local part string to validate
 * @returns true if valid, false otherwise
 */
const isValidLocalPart = (localPart: string | undefined): boolean => {
  if (!localPart || localPart.length === 0) {
    return false;
  }

  // Check valid characters
  if (!LOCAL_PART_PATTERN.test(localPart)) {
    return false;
  }

  // Cannot start or end with a dot
  if (localPart.startsWith(".") || localPart.endsWith(".")) {
    return false;
  }

  // No consecutive dots
  return !localPart.includes("..");
};

/**
 * Validates the domain part of an email address (after @).
 *
 * @param domainPart - The domain part string to validate
 * @returns true if valid, false otherwise
 */
const isValidDomainPart = (domainPart: string | undefined): boolean => {
  if (!domainPart || domainPart.length === 0) {
    return false;
  }

  // Check valid characters
  if (!DOMAIN_PATTERN.test(domainPart)) {
    return false;
  }

  // Cannot start or end with a dot or hyphen
  if (
    domainPart.startsWith(".") ||
    domainPart.endsWith(".") ||
    domainPart.startsWith("-") ||
    domainPart.endsWith("-")
  ) {
    return false;
  }

  // No consecutive dots
  if (domainPart.includes("..")) {
    return false;
  }

  // Split domain into parts (e.g., "example.com" -> ["example", "com"])
  const domainParts = domainPart.split(".");

  // Must have at least two parts (domain and TLD)
  if (domainParts.length < 2) {
    return false;
  }

  // TLD (last part) must not be empty
  const tld = domainParts[domainParts.length - 1];

  if (tld.length === 0) {
    return false;
  }

  // Each domain part must not be empty and cannot start or end with a hyphen
  return domainParts.every(
    (part) => part.length > 0 && !part.startsWith("-") && !part.endsWith("-"),
  );
};

export const getEmailValidationSchema = (isRequired = true) => {
  const invalidEmailMessage = m.formValidation_invalid({
    name: m.formFields_email(),
  });
  const requiredMessage = m.formValidation_required({
    name: m.formFields_email(),
  });

  const baseSchema = Yup.string()
    .test("email-format", invalidEmailMessage, (value) => {
      if (!value || value.trim() === "") {
        return !isRequired; // Allow empty if optional
      }

      return validateEmailFormat(value);
    })
    .max(MAX_EMAIL_LENGTH, invalidEmailMessage)
    .nullable();

  if (isRequired) {
    return baseSchema.required(requiredMessage);
  }

  return baseSchema.optional();
};

export const getPercentageValidation = (isRequired = true) => {
  const minMaxErrorMessage = m.formValidation_percentage({
    min: 0,
    max: 100,
  });

  return isRequired
    ? Yup.number()
        .required(
          m.formValidation_required({
            name: m.formFields_percentage(),
          }),
        )
        .min(0, minMaxErrorMessage)
        .max(100, minMaxErrorMessage)
        .nullable()
    : Yup.number().min(0, minMaxErrorMessage).max(100, minMaxErrorMessage);
};

/**
 * Handles the validation for number fields and allows empty values
 */
export const getNumberValidation = (
  isRequired: boolean,
  requiredErrorMessage?: string,
) => {
  const numberSchema = Yup.number()
    .transform((value, originalValue) => (originalValue === "" ? null : value))
    .nullable();

  return isRequired
    ? numberSchema.required(requiredErrorMessage)
    : numberSchema;
};

export const getAddressValidation = (
  isRequired = true,
  validateZipCodes = false,
) => {
  const validationSchema = Yup.object({
    addressLine1: Yup.string().required(
      m.formValidation_required({ name: m.formFields_addressLine1() }),
    ),
    addressLine2: Yup.string().nullable(),
    city: Yup.string().required(
      m.formValidation_required({ name: m.formFields_city() }),
    ),
    country: Yup.string()
      .required(m.formValidation_required({ name: m.formFields_country() }))
      .nullable(),
    state: Yup.string().nullable(),
    zipcode: Yup.string()
      .nullable()
      .test(
        "zipcode-validation",
        m.formValidation_invalidZipcodeForCountry(),
        function (value) {
          const { country } = this.parent;

          // If no country is selected, skip zipcode validation
          // The country field validation will handle the requirement
          if (!country) {
            return true;
          }

          // Check if zipcode is optional for this country
          const isOptional = isZipcodeOptionalForCountry(country);

          // If zipcode is optional and no value provided, it's valid
          if (isOptional && !value) return true;

          // If zipcode is required but no value provided, it's invalid
          if (!isOptional && !value) {
            return this.createError({
              message: m.formValidation_required({
                name: m.formFields_postalCode(),
              }),
            });
          }

          // If value is provided, validate the format
          if (value && validateZipCodes) {
            return validateZipCode(country, value);
          }

          return true;
        },
      ),
  }).nullable();

  return Yup.object()
    .nullable()
    .test("address-validation", "", function (value, context) {
      if (!isRequired && (!value || isEqual(value, getEmptyAddress()))) {
        return true;
      }

      try {
        validationSchema.validateSync(value, {
          abortEarly: false,
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-expect-error
          path: context.path,
        });

        return true;
      } catch (err) {
        return err as ValidationError;
      }
    });
};

export const getOptionalAddressValidation = (validateZipCodes = false) => {
  const validationSchema = Yup.object({
    addressLine1: Yup.string().nullable(),
    addressLine2: Yup.string().nullable(),
    city: Yup.string().nullable(),
    country: Yup.string().nullable(),
    state: Yup.string().nullable(),
    zipcode: validateZipCodes
      ? Yup.string()
          .nullable()
          .test(
            "zipcode-validation",
            m.formValidation_invalidZipcodeForCountry(),
            function (value) {
              // Only validate if value is provided
              if (!value) return true;

              const { country } = this.parent;

              return validateZipCode(country, value);
            },
          )
      : Yup.string().nullable(),
  }).nullable();

  return validationSchema;
};

export const getNameValidation = () =>
  Yup.object({
    firstName: Yup.string()
      .min(1)
      .required(
        m.formValidation_required({
          name: m.formFields_firstName(),
        }),
      )
      .nullable(),
    lastName: Yup.string()
      .min(1)
      .required(
        m.formValidation_required({
          name: m.formFields_lastName(),
        }),
      )
      .nullable(),
  }).nullable();
export const getNameWithTitleValidation = () =>
  Yup.object({
    firstName: Yup.string()
      .min(1)
      .required(
        m.formValidation_required({
          name: m.formFields_firstName(),
        }),
      )
      .nullable(),
    lastName: Yup.string()
      .min(1)
      .required(
        m.formValidation_required({
          name: m.formFields_lastName(),
        }),
      )
      .nullable(),
    title: Yup.string()
      .min(1)
      .required(
        m.formValidation_required({
          name: m.formFields_titlePosition(),
        }),
      )
      .nullable(),
  }).nullable();

export const getEmailValidation = () =>
  Yup.string()
    .email(m.formValidation_invalid({ name: m.formFields_email() }))
    .required(
      m.formValidation_required({
        name: m.formFields_email(),
      }),
    )
    .nullable();

export const getBirthdayValidation = () =>
  Yup.date()
    .required(
      m.formValidation_required({
        name: m.formFields_dateOfBirth(),
      }),
    )
    .nullable();
// Validation for addresses fields which includes:
// Mailing, Permanent and MailingSameAsPermanent fields
export const getAddressesWithMailValidations = () =>
  Yup.object({
    mailingAddress: Yup.object().when("mailingSameAsPermanent", {
      is: false,
      otherwise: Yup.object().shape({}).nullable(), // Optional if not required
      then: getAddressWithContactsValidation(),
    }),
    mailingSameAsPermanent: Yup.boolean(),
    permanentAddress: getAddressWithContactsValidation(),
  });

export const getAddressWithContactsValidation = () =>
  Yup.object()
    .shape({
      address: getAddressValidation(),
      email: Yup.string()
        .required(
          m.formValidation_required({
            name: m.formFields_email(),
          }),
        )
        .email(m.formValidation_invalid({ name: m.formFields_email() })),
      telephone: getPhoneValidationSchema(),
    })
    .nullable();

export const getEmptyName = () => ({
  firstName: undefined,
  lastName: undefined,
});

export const getEmptyNameWithMiddle = () => ({
  firstName: undefined,
  middleName: undefined,
  lastName: undefined,
});

export const getEmptyNameWithTitle = () => ({
  firstName: undefined,
  lastName: undefined,
  title: undefined,
  middleName: undefined,
});

export const getEmptyAddress = () => ({
  addressLine1: undefined,
  addressLine2: undefined,
  city: undefined,
  country: undefined,
  state: undefined,
  zipcode: undefined,
});

export const getEmptyAddressWithContact = () => ({
  address: getEmptyAddress(),
  email: "",
  attention: "",
  telephone: "",
});

export const getEmptySameAsPrimaryAddress = () => ({
  mailingAddress: getEmptyAddressWithContact(),
  mailingSameAsPermanent: true,
  permanentAddress: getEmptyAddressWithContact(),
});

export const isUnitedStates = (countryNameOrCode?: string) =>
  countryNameOrCode === "United States" || countryNameOrCode === "US"; // deprecate when migration to alpha2 is done

// Nullable to prevent errors when fetching answers with null values

export function useTouchFieldOnSubmit(name: string) {
  const [, , helpers] = useField(name);

  const formik = useFormikContext();
  const isSubmitting = formik.isSubmitting;

  useEffect(() => {
    if (isSubmitting) {
      helpers.setTouched(true);
    }
  }, [isSubmitting, helpers]);
}
